import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { 
  saveToLocalStorage, 
  loadFromLocalStorage, 
  getDefaultState,
  exportToJSON,
  importFromJSON 
} from '../utils/storage';
import { generateId, processRecurringTransactions } from '../utils/helpers';

// Create context
const FinanceContext = createContext(null);

// Action types
const ACTIONS = {
  SET_STATE: 'SET_STATE',
  COMPLETE_ONBOARDING: 'COMPLETE_ONBOARDING',
  ADD_TRANSACTION: 'ADD_TRANSACTION',
  UPDATE_TRANSACTION: 'UPDATE_TRANSACTION',
  DELETE_TRANSACTION: 'DELETE_TRANSACTION',
  ADD_PERSON: 'ADD_PERSON',
  UPDATE_PERSON: 'UPDATE_PERSON',
  DELETE_PERSON: 'DELETE_PERSON',
  ADD_LENDING: 'ADD_LENDING',
  UPDATE_LENDING: 'UPDATE_LENDING',
  DELETE_LENDING: 'DELETE_LENDING',
  SETTLE_LENDING_PARTIAL: 'SETTLE_LENDING_PARTIAL',
  ADD_BORROWING: 'ADD_BORROWING',
  UPDATE_BORROWING: 'UPDATE_BORROWING',
  DELETE_BORROWING: 'DELETE_BORROWING',
  SETTLE_BORROWING_PARTIAL: 'SETTLE_BORROWING_PARTIAL',
  ADD_CATEGORY: 'ADD_CATEGORY',
  UPDATE_CATEGORY: 'UPDATE_CATEGORY',
  DELETE_CATEGORY: 'DELETE_CATEGORY',
  ADD_TAG: 'ADD_TAG',
  UPDATE_TAG: 'UPDATE_TAG',
  DELETE_TAG: 'DELETE_TAG',
  ADD_MODE: 'ADD_MODE',
  UPDATE_MODE: 'UPDATE_MODE',
  DELETE_MODE: 'DELETE_MODE',
  ADD_RECURRING: 'ADD_RECURRING',
  UPDATE_RECURRING: 'UPDATE_RECURRING',
  DELETE_RECURRING: 'DELETE_RECURRING',
  TOGGLE_RECURRING: 'TOGGLE_RECURRING',
  PROCESS_RECURRING: 'PROCESS_RECURRING',
  SET_CURRENCY: 'SET_CURRENCY',
  IMPORT_DATA: 'IMPORT_DATA',
  RESET_DATA: 'RESET_DATA',
};

// Reducer
function financeReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_STATE:
      return { ...state, ...action.payload };

    case ACTIONS.COMPLETE_ONBOARDING:
      return { ...state, isOnboarded: true };

    case ACTIONS.ADD_TRANSACTION:
      return { 
        ...state, 
        transactions: [action.payload, ...state.transactions] 
      };

    case ACTIONS.UPDATE_TRANSACTION:
      return { 
        ...state, 
        transactions: state.transactions.map(t => 
          t.id === action.payload.id ? action.payload : t
        ) 
      };

    case ACTIONS.DELETE_TRANSACTION:
      return { 
        ...state, 
        transactions: state.transactions.filter(t => t.id !== action.payload) 
      };

    case ACTIONS.ADD_PERSON:
      return { 
        ...state, 
        people: [...state.people, action.payload] 
      };

    case ACTIONS.UPDATE_PERSON:
      return { 
        ...state, 
        people: state.people.map(p => 
          p.id === action.payload.id ? action.payload : p
        ) 
      };

    case ACTIONS.DELETE_PERSON:
      return { 
        ...state, 
        people: state.people.filter(p => p.id !== action.payload) 
      };

    case ACTIONS.ADD_LENDING:
      return { 
        ...state, 
        lendings: [...state.lendings, action.payload] 
      };

    case ACTIONS.UPDATE_LENDING:
      return { 
        ...state, 
        lendings: state.lendings.map(l => 
          l.id === action.payload.id ? action.payload : l
        ) 
      };

    case ACTIONS.DELETE_LENDING:
      return { 
        ...state, 
        lendings: state.lendings.filter(l => l.id !== action.payload) 
      };

    case ACTIONS.SETTLE_LENDING_PARTIAL: {
      const { id, amount, date, modeId } = action.payload;
      return { 
        ...state, 
        lendings: state.lendings.map(l => {
          if (l.id !== id) return l;
          const currentSettled = (l.settlements || []).reduce((acc, s) => acc + s.amount, 0);
          const newSettledAmount = currentSettled + amount;
          const isSettled = newSettledAmount >= l.amount;
          return {
            ...l,
            isSettled,
            settledDate: isSettled ? date : null,
            settlements: [...(l.settlements || []), { amount, date, modeId }]
          };
        }) 
      };
    }

    case ACTIONS.ADD_BORROWING:
      return { 
        ...state, 
        borrowings: [...state.borrowings, action.payload] 
      };

    case ACTIONS.UPDATE_BORROWING:
      return { 
        ...state, 
        borrowings: state.borrowings.map(b => 
          b.id === action.payload.id ? action.payload : b
        ) 
      };

    case ACTIONS.DELETE_BORROWING:
      return { 
        ...state, 
        borrowings: state.borrowings.filter(b => b.id !== action.payload) 
      };

    case ACTIONS.SETTLE_BORROWING_PARTIAL: {
      const { id, amount, date, modeId } = action.payload;
      return { 
        ...state, 
        borrowings: state.borrowings.map(b => {
          if (b.id !== id) return b;
          const currentSettled = (b.settlements || []).reduce((acc, s) => acc + s.amount, 0);
          const newSettledAmount = currentSettled + amount;
          const isSettled = newSettledAmount >= b.amount;
          return {
            ...b,
            isSettled,
            settledDate: isSettled ? date : null,
            settlements: [...(b.settlements || []), { amount, date, modeId }]
          };
        }) 
      };
    }

    case ACTIONS.ADD_CATEGORY:
      return { 
        ...state, 
        categories: [...state.categories, action.payload] 
      };

    case ACTIONS.UPDATE_CATEGORY:
      return { 
        ...state, 
        categories: state.categories.map(c => 
          c.id === action.payload.id ? action.payload : c
        ) 
      };

    case ACTIONS.DELETE_CATEGORY:
      return { 
        ...state, 
        categories: state.categories.filter(c => c.id !== action.payload) 
      };

    case ACTIONS.ADD_TAG:
      return { 
        ...state, 
        tags: [...state.tags, action.payload] 
      };

    case ACTIONS.UPDATE_TAG:
      return { 
        ...state, 
        tags: state.tags.map(t => 
          t.id === action.payload.id ? action.payload : t
        ) 
      };

    case ACTIONS.DELETE_TAG:
      return { 
        ...state, 
        tags: state.tags.filter(t => t.id !== action.payload) 
      };

    case ACTIONS.ADD_MODE:
      return { 
        ...state, 
        modes: [...(state.modes || []), action.payload] 
      };

    case ACTIONS.UPDATE_MODE:
      return { 
        ...state, 
        modes: (state.modes || []).map(m => 
          m.id === action.payload.id ? action.payload : m
        ) 
      };

    case ACTIONS.DELETE_MODE:
      return { 
        ...state, 
        modes: (state.modes || []).filter(m => m.id !== action.payload) 
      };

    case ACTIONS.ADD_RECURRING:
      return { 
        ...state, 
        recurringTransactions: [...state.recurringTransactions, action.payload] 
      };

    case ACTIONS.UPDATE_RECURRING:
      return { 
        ...state, 
        recurringTransactions: state.recurringTransactions.map(r => 
          r.id === action.payload.id ? action.payload : r
        ) 
      };

    case ACTIONS.DELETE_RECURRING:
      return { 
        ...state, 
        recurringTransactions: state.recurringTransactions.filter(r => r.id !== action.payload) 
      };

    case ACTIONS.TOGGLE_RECURRING:
      return { 
        ...state, 
        recurringTransactions: state.recurringTransactions.map(r => 
          r.id === action.payload ? { ...r, isActive: !r.isActive } : r
        ) 
      };

    case ACTIONS.PROCESS_RECURRING:
      return { 
        ...state, 
        transactions: [...action.payload.newTransactions, ...state.transactions],
        recurringTransactions: action.payload.updatedRecurring,
        lastRecurringCheck: new Date().toISOString(),
      };

    case ACTIONS.SET_CURRENCY:
      return { ...state, currency: action.payload };

    case ACTIONS.IMPORT_DATA:
      return { ...getDefaultState(), ...action.payload };

    case ACTIONS.RESET_DATA:
      return getDefaultState();

    default:
      return state;
  }
}

// Provider component
export function FinanceProvider({ children }) {
  const [state, dispatch] = useReducer(financeReducer, getDefaultState());
  const [isLoaded, setIsLoaded] = React.useState(false);

  // Load data from localStorage on mount
  useEffect(() => {
    const savedData = loadFromLocalStorage();
    if (savedData) {
      // Ensure modes exist for legacy data
      if (!savedData.modes) {
        savedData.modes = getDefaultState().modes;
      }
      dispatch({ type: ACTIONS.SET_STATE, payload: savedData });
    }
    setIsLoaded(true);
  }, []);

  // Save to localStorage whenever state changes
  useEffect(() => {
    if (isLoaded) {
      saveToLocalStorage(state);
    }
  }, [state, isLoaded]);

  // Process recurring transactions on load
  useEffect(() => {
    if (isLoaded && state.recurringTransactions.length > 0) {
      const { newTransactions, updatedRecurring } = processRecurringTransactions(
        state.recurringTransactions,
        state.lastRecurringCheck
      );
      
      if (newTransactions.length > 0) {
        dispatch({ 
          type: ACTIONS.PROCESS_RECURRING, 
          payload: { newTransactions, updatedRecurring } 
        });
      }
    }
  }, [isLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Action creators
  const actions = {
    completeOnboarding: useCallback(() => {
      dispatch({ type: ACTIONS.COMPLETE_ONBOARDING });
    }, []),

    addTransaction: useCallback((transaction) => {
      dispatch({ 
        type: ACTIONS.ADD_TRANSACTION, 
        payload: { ...transaction, id: generateId(), createdAt: new Date().toISOString() } 
      });
    }, []),

    updateTransaction: useCallback((transaction) => {
      dispatch({ type: ACTIONS.UPDATE_TRANSACTION, payload: transaction });
    }, []),

    deleteTransaction: useCallback((id) => {
      dispatch({ type: ACTIONS.DELETE_TRANSACTION, payload: id });
    }, []),

    addPerson: useCallback((person) => {
      dispatch({ 
        type: ACTIONS.ADD_PERSON, 
        payload: { ...person, id: generateId() } 
      });
    }, []),

    updatePerson: useCallback((person) => {
      dispatch({ type: ACTIONS.UPDATE_PERSON, payload: person });
    }, []),

    deletePerson: useCallback((id) => {
      dispatch({ type: ACTIONS.DELETE_PERSON, payload: id });
    }, []),

    addLending: useCallback((lending) => {
      dispatch({ 
        type: ACTIONS.ADD_LENDING, 
        payload: { 
          ...lending, 
          id: generateId(), 
          isSettled: false, 
          settlements: [],
          createdAt: new Date().toISOString() 
        } 
      });
    }, []),

    updateLending: useCallback((lending) => {
      dispatch({ type: ACTIONS.UPDATE_LENDING, payload: lending });
    }, []),

    deleteLending: useCallback((id) => {
      dispatch({ type: ACTIONS.DELETE_LENDING, payload: id });
    }, []),

    settleLending: useCallback((id, amount, modeId) => {
      dispatch({ 
        type: ACTIONS.SETTLE_LENDING_PARTIAL, 
        payload: { 
          id, 
          amount, 
          modeId,
          date: new Date().toISOString() 
        } 
      });
    }, []),

    addBorrowing: useCallback((borrowing) => {
      dispatch({ 
        type: ACTIONS.ADD_BORROWING, 
        payload: { 
          ...borrowing, 
          id: generateId(), 
          isSettled: false, 
          settlements: [],
          createdAt: new Date().toISOString() 
        } 
      });
    }, []),

    updateBorrowing: useCallback((borrowing) => {
      dispatch({ type: ACTIONS.UPDATE_BORROWING, payload: borrowing });
    }, []),

    deleteBorrowing: useCallback((id) => {
      dispatch({ type: ACTIONS.DELETE_BORROWING, payload: id });
    }, []),

    settleBorrowing: useCallback((id, amount, modeId) => {
      dispatch({ 
        type: ACTIONS.SETTLE_BORROWING_PARTIAL, 
        payload: { 
          id, 
          amount, 
          modeId,
          date: new Date().toISOString() 
        } 
      });
    }, []),

    addCategory: useCallback((category) => {
      dispatch({ 
        type: ACTIONS.ADD_CATEGORY, 
        payload: { ...category, id: generateId() } 
      });
    }, []),

    updateCategory: useCallback((category) => {
      dispatch({ type: ACTIONS.UPDATE_CATEGORY, payload: category });
    }, []),

    deleteCategory: useCallback((id) => {
      dispatch({ type: ACTIONS.DELETE_CATEGORY, payload: id });
    }, []),

    addTag: useCallback((tag) => {
      dispatch({ 
        type: ACTIONS.ADD_TAG, 
        payload: { ...tag, id: generateId() } 
      });
    }, []),

    updateTag: useCallback((tag) => {
      dispatch({ type: ACTIONS.UPDATE_TAG, payload: tag });
    }, []),

    deleteTag: useCallback((id) => {
      dispatch({ type: ACTIONS.DELETE_TAG, payload: id });
    }, []),

    addMode: useCallback((mode) => {
      dispatch({ 
        type: ACTIONS.ADD_MODE, 
        payload: { ...mode, id: generateId() } 
      });
    }, []),

    updateMode: useCallback((mode) => {
      dispatch({ type: ACTIONS.UPDATE_MODE, payload: mode });
    }, []),

    deleteMode: useCallback((id) => {
      dispatch({ type: ACTIONS.DELETE_MODE, payload: id });
    }, []),

    addRecurring: useCallback((recurring) => {
      dispatch({ 
        type: ACTIONS.ADD_RECURRING, 
        payload: { ...recurring, id: generateId(), isActive: true, createdAt: new Date().toISOString() } 
      });
    }, []),

    updateRecurring: useCallback((recurring) => {
      dispatch({ type: ACTIONS.UPDATE_RECURRING, payload: recurring });
    }, []),

    deleteRecurring: useCallback((id) => {
      dispatch({ type: ACTIONS.DELETE_RECURRING, payload: id });
    }, []),

    toggleRecurring: useCallback((id) => {
      dispatch({ type: ACTIONS.TOGGLE_RECURRING, payload: id });
    }, []),

    setCurrency: useCallback((currency) => {
      dispatch({ type: ACTIONS.SET_CURRENCY, payload: currency });
    }, []),

    exportData: useCallback(() => {
      exportToJSON(state);
    }, [state]),

    importData: useCallback(async (file) => {
      try {
        const data = await importFromJSON(file);
        // Clean data for migrations if needed
        if (!data.modes) data.modes = getDefaultState().modes;
        dispatch({ type: ACTIONS.IMPORT_DATA, payload: data });
        return { success: true };
      } catch (error) {
        return { success: false, error: error.message };
      }
    }, []),

    resetData: useCallback(() => {
      dispatch({ type: ACTIONS.RESET_DATA });
    }, []),
  };

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <FinanceContext.Provider value={{ state, ...actions }}>
      {children}
    </FinanceContext.Provider>
  );
}

// Custom hook to use finance context
export function useFinance() {
  const context = useContext(FinanceContext);
  if (!context) {
    throw new Error('useFinance must be used within a FinanceProvider');
  }
  return context;
}

export default FinanceContext;
